'use client';

import { HStack, Box, VStack } from '@terra/ui-styled-system';
import { Add01Icon, SearchVisualIcon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import {
  validateDomain,
  type AssetValidationResult,
} from '@terra/shared-utils';
import type {
  DomainDto,
  NetBlockDto,
} from '@terra/shared-contracts-external-network';
import { Alert } from '../../../components/alert';
import { Button } from '../../../components/button';
import { Spinner } from '../../../components/spinner';
import { Tabs } from '../../../components/tabs';
import { ConfirmationDialog } from '../../confirmation-dialog/ConfirmationDialog';
import { SurfaceScopeAddDialog } from '../../surface-scope/SurfaceScopeAddDialog';
import { DomainsSection } from './DomainsSection';
import { NetblocksSection } from './NetblocksSection';
import { ScopeFilterToolbar } from './ScopeFilterToolbar';
import { ScrollLoadTrigger } from '../../scroll-load-trigger';
import type {
  ScopeCounts,
  ScopeEntityChange,
  ScopeFilter,
  ScopeSection,
} from './scope-types';

export type ExternalScopeManagementContentProps = {
  isScanActive: boolean;
  isReadOnly: boolean;
  isDisabled: boolean;
  activeSection: ScopeSection;
  onSectionChange: (section: ScopeSection) => void;
  netblocksTotal: number;
  domainsTotal: number;
  scopeFilter: ScopeFilter;
  scopeCounts: ScopeCounts;
  onFilterChange: (filter: ScopeFilter) => void;
  selectedCount: number;
  onBulkApprove: () => void;
  onBulkReject: () => void;
  netblocks: NetBlockDto[];
  isNetblocksLoading: boolean;
  selectedNetblockIds: Set<string>;
  onToggleNetblockSelection: (id: string) => void;
  onNetblockScopeChange: (change: ScopeEntityChange) => void;
  hasMoreNetblocks: boolean;
  canLoadMoreNetblocks: boolean;
  isLoadingMoreNetblocks: boolean;
  onLoadMoreNetblocks: () => void;
  domains: DomainDto[];
  domainTreeTotal: number;
  isDomainsLoading: boolean;
  selectedDomainIds: string[];
  onDomainSelectionChange: (ids: string[]) => void;
  onDomainScopeChange: (change: ScopeEntityChange) => void;
  isAddPending: boolean;
  onAddClick: () => void;
  isAddDomainDialogOpen: boolean;
  onAddDomainDialogOpenChange: (open: boolean) => void;
  isAddNetblockDialogOpen: boolean;
  onAddNetblockDialogOpenChange: (open: boolean) => void;
  onAddDomains: (values: string[]) => Promise<void>;
  onAddNetblocks: (values: string[]) => Promise<void>;
  isDomainAddPending: boolean;
  isNetblockAddPending: boolean;
  existingDomainValues: string[];
  existingNetblockValues: string[];
  netblockValidator: (value: string) => AssetValidationResult;
  isHostingConfirmOpen: boolean;
  onHostingConfirmCancel: () => void;
  onHostingConfirm: () => void;
};

const isScopeSection = (value: string): value is ScopeSection =>
  value === 'netblocks' || value === 'domains';

export function ExternalScopeManagementContent({
  isScanActive,
  isReadOnly,
  isDisabled,
  activeSection,
  onSectionChange,
  netblocksTotal,
  domainsTotal,
  scopeFilter,
  scopeCounts,
  onFilterChange,
  selectedCount,
  onBulkApprove,
  onBulkReject,
  netblocks,
  isNetblocksLoading,
  selectedNetblockIds,
  onToggleNetblockSelection,
  onNetblockScopeChange,
  hasMoreNetblocks,
  canLoadMoreNetblocks,
  isLoadingMoreNetblocks,
  onLoadMoreNetblocks,
  domains,
  domainTreeTotal,
  isDomainsLoading,
  selectedDomainIds,
  onDomainSelectionChange,
  onDomainScopeChange,
  isAddPending,
  onAddClick,
  isAddDomainDialogOpen,
  onAddDomainDialogOpenChange,
  isAddNetblockDialogOpen,
  onAddNetblockDialogOpenChange,
  onAddDomains,
  onAddNetblocks,
  isDomainAddPending,
  isNetblockAddPending,
  existingDomainValues,
  existingNetblockValues,
  netblockValidator,
  isHostingConfirmOpen,
  onHostingConfirmCancel,
  onHostingConfirm,
}: ExternalScopeManagementContentProps) {
  const t = useTranslations('designSystem.networkSettings.externalScope');
  const tCommon = useTranslations('designSystem.common');

  return (
    <VStack gap={0} w="full" alignItems="stretch">
      {isScanActive && (
        <Alert.Root
          mb={4}
          background="bg.warning.subtle"
          borderColor="border.warning.subtle"
        >
          <Alert.Icon asChild color="fg.warning.default">
            <SearchVisualIcon size={16} />
          </Alert.Icon>
          <Alert.Content>
            <Alert.Title>{t('scanLockedTitle')}</Alert.Title>
            <Alert.Description>{t('scanActiveNotice')}</Alert.Description>
          </Alert.Content>
        </Alert.Root>
      )}
      <Tabs.Root
        value={activeSection}
        size="sm"
        onValueChange={({ value }) => {
          if (isScopeSection(value)) {
            onSectionChange(value);
          }
        }}
      >
        <Tabs.List>
          <Tabs.Trigger value="netblocks">
            {`${t('netblocksSection')} (${netblocksTotal})`}
          </Tabs.Trigger>
          <Tabs.Trigger value="domains">
            {`${t('domainsSection')} (${domainsTotal})`}
          </Tabs.Trigger>
          {!isReadOnly ? (
            <HStack flex={1} justifyContent="flex-end" pb={1}>
              <Button
                size="xs"
                variant="ghost"
                disabled={isDisabled || isAddPending}
                onClick={onAddClick}
              >
                <Add01Icon size={16} />
                {tCommon('add')}
              </Button>
            </HStack>
          ) : null}
          <Tabs.Indicator />
        </Tabs.List>

        <Box pt={4}>
          <ScopeFilterToolbar
            scopeFilter={scopeFilter}
            scopeCounts={scopeCounts}
            selectedCount={selectedCount}
            isDisabled={isDisabled}
            onFilterChange={onFilterChange}
            onBulkApprove={onBulkApprove}
            onBulkReject={onBulkReject}
          />

          <Tabs.Content value="netblocks">
            <NetblocksSection
              netblocks={netblocks}
              isLoading={isNetblocksLoading}
              selectedIds={selectedNetblockIds}
              isDisabled={isDisabled}
              onToggleSelection={onToggleNetblockSelection}
              onScopeChange={onNetblockScopeChange}
            />
            {hasMoreNetblocks ? (
              <ScrollLoadTrigger
                isEnabled={canLoadMoreNetblocks}
                onLoadMore={onLoadMoreNetblocks}
                dataTestid="scope-scroll-load-trigger"
              />
            ) : null}
            {isLoadingMoreNetblocks ? (
              <HStack justifyContent="center" py={3}>
                <Spinner size="sm" />
              </HStack>
            ) : null}
          </Tabs.Content>

          <Tabs.Content value="domains">
            <DomainsSection
              domains={domains}
              totalDomains={domainTreeTotal}
              isLoading={isDomainsLoading}
              selectedIds={selectedDomainIds}
              scopeFilter={scopeFilter}
              isDisabled={isDisabled}
              onSelectionChange={onDomainSelectionChange}
              onScopeChange={onDomainScopeChange}
            />
          </Tabs.Content>
        </Box>
      </Tabs.Root>

      <SurfaceScopeAddDialog
        open={isAddDomainDialogOpen}
        onOpenChange={onAddDomainDialogOpenChange}
        onAdd={onAddDomains}
        title={`${tCommon('add')} ${t('targetDomains')}`}
        description={t('bulkDomainsDescription')}
        label={t('targetDomains')}
        placeholder={t('targetDomainsPlaceholder')}
        helperText={t('targetDomainsHelper')}
        validator={validateDomain}
        duplicateErrorMessage={t('duplicateError')}
        existingValues={existingDomainValues}
        isAdding={isDomainAddPending}
      />

      <SurfaceScopeAddDialog
        open={isAddNetblockDialogOpen}
        onOpenChange={onAddNetblockDialogOpenChange}
        onAdd={onAddNetblocks}
        title={`${tCommon('add')} ${t('targetRanges')}`}
        description={t('bulkNetblocksDescription')}
        label={t('targetRanges')}
        placeholder={t('targetRangesPlaceholder')}
        helperText={t('targetRangesHelper')}
        validator={netblockValidator}
        duplicateErrorMessage={t('duplicateError')}
        existingValues={existingNetblockValues}
        isAdding={isNetblockAddPending}
      />
      <ConfirmationDialog
        isOpen={isHostingConfirmOpen}
        onOpenChange={({ open }) => {
          if (!open) {
            onHostingConfirmCancel();
          }
        }}
        variant="warning"
        title={t('hostingConfirmTitle')}
        description={t('hostingConfirmDescription')}
        confirmText={t('hostingConfirm')}
        onConfirm={onHostingConfirm}
      />
    </VStack>
  );
}
